import type Phaser from "phaser";
import { visualTravelMs } from "../../../vfx/attack-fx";
import { FlDepth } from "./fl-layers";

/** Travel duration scales slightly with distance; clamped for readability. */
export function boltTravelMs(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
): number {
  return visualTravelMs(x0, y0, x1, y1, {
    kind: "bolt",
    mul: 0.55,
    minMs: 120,
    maxMs: 360,
  });
}

/** Slower, heavier shell travel for siege. */
export function siegeShellTravelMs(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
): number {
  return visualTravelMs(x0, y0, x1, y1, { kind: "siege" });
}

export function isSiegeBoltRole(role: string): boolean {
  return role === "fl_t3_siege" || role.includes("siege");
}

/**
 * Ellipse bolt that tweens from muzzle to target (FFA-style traveling round).
 * `df.flight.bolt` remains a short muzzle flash — this is the visible projectile.
 */
export function spawnBoltTrail(
  scene: Phaser.Scene,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  ms: number,
  color: number,
  heavy = false,
  onComplete?: () => void,
): void {
  const w = heavy ? 24 : 18;
  const h = heavy ? 12 : 8;
  const angle = Math.atan2(y1 - y0, x1 - x0);
  const bolt = scene.add
    .ellipse(x0, y0, w, h, color, 1)
    .setDepth(FlDepth.fx)
    .setRotation(angle)
    .setStrokeStyle(2, 0xffffff, heavy ? 0.85 : 0.7);
  // Soft trailing ghost so path reads against crowded boards.
  const ghost = scene.add
    .ellipse(x0, y0, w * 1.35, h * 1.2, color, 0.35)
    .setDepth(FlDepth.fx)
    .setRotation(angle);
  const duration = Math.max(16, Math.round(ms));
  scene.tweens.add({
    targets: [bolt, ghost],
    x: x1,
    y: y1,
    duration,
    ease: heavy ? "Cubic.easeIn" : "Cubic.easeOut",
    onComplete: () => {
      bolt.destroy();
      ghost.destroy();
      onComplete?.();
    },
  });
  scene.tweens.add({
    targets: ghost,
    alpha: 0.08,
    scaleX: 1.4,
    scaleY: 1.25,
    duration,
    ease: "Linear",
  });
}
